import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import VideoSidebar from './VideoSidebar';
import WeatherWidget from './WeatherWidget';
import GolfNewsWidget from './GolfNewsWidget';
import LeaderboardWidget from './LeaderboardWidget';
import NotificationBell from './NotificationBell';
import VenmoDonate from './VenmoDonate';
import { House, Compass, Clipboard, MapPin, Storefront, User, Upload, SignOut } from '@phosphor-icons/react';

const NAV_ITEMS = [
  { path: '/', label: 'Feed', icon: House },
  { path: '/profile', label: 'Profile', icon: User },
  { path: '/scorecard', label: 'Scorecard', icon: Clipboard },
  { path: '/play', label: 'Play', icon: MapPin },
  { path: '/shop', label: 'Shop', icon: Storefront },
  { path: '/upload', label: 'Upload', icon: Upload },
  { path: '/explore', label: 'Explore', icon: Compass },
];

const FULL_WIDTH_PAGES = ['/auth', '/upload', '/play'];

export default function DesktopLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const isFullWidth = FULL_WIDTH_PAGES.some(p => location.pathname.startsWith(p));

  if (isFullWidth) return <>{children}</>;

  return (
    <div className="desktop-layout" data-testid="desktop-layout">
      {/* Left Sidebar */}
      <aside className="desktop-sidebar-left" data-testid="sidebar-left">
        <div className="sidebar-inner">
          <div className="sidebar-brand">
            <h2 className="font-serif text-xl text-primary font-semibold tracking-tight cursor-pointer" onClick={() => navigate('/')}>GolfScroll</h2>
            <p className="text-[10px] text-[#D4AF37] uppercase tracking-[0.2em] font-sans font-semibold mt-0.5">The Social Club</p>
          </div>

          {/* Desktop Nav */}
          <nav className="flex flex-col gap-0.5" data-testid="desktop-nav">
            {NAV_ITEMS.map(item => {
              const active = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-sans transition-colors duration-200 text-left ${
                    active
                      ? 'bg-primary/5 text-primary font-semibold'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                  }`}
                  data-testid={`desktop-nav-${item.label.toLowerCase()}`}
                >
                  <item.icon size={18} weight={active ? 'fill' : 'regular'} />
                  {item.label}
                </button>
              );
            })}
            {user && (
              <button
                onClick={async () => { await logout(); navigate('/auth'); }}
                className="flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-sans text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors duration-200 text-left mt-2 border-t border-border pt-3"
                data-testid="desktop-nav-signout"
              >
                <SignOut size={18} />
                Sign Out
              </button>
            )}
          </nav>

          <div className="mt-auto" />
          <div className="px-3 py-3">
            <VenmoDonate />
          </div>
          <VideoSidebar />
        </div>
      </aside>

      {/* Center Content */}
      <main className="desktop-main" data-testid="main-content">
        {children}
      </main>

      {/* Right Sidebar - Weather, Leaderboard & News */}
      <aside className="desktop-sidebar-right" data-testid="sidebar-right">
        <div className="sidebar-inner">
          {/* Notification bell at top of sidebar */}
          {user && (
            <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
              <span className="text-xs font-sans font-bold uppercase tracking-wider text-muted-foreground">Updates</span>
              <NotificationBell />
            </div>
          )}
          <WeatherWidget />
          <LeaderboardWidget />
          <GolfNewsWidget />
        </div>
      </aside>
    </div>
  );
}
